import React from 'react';
import { cn } from '@/lib/utils';
import '@/design-system/typography.css';

/**
 * GenericKPIDashboard -- the one compact KPI bar for list pages. It owns
 * ALL the look (typography, spacing, border, background, tiles, responsive
 * columns); a module only says WHICH KPIs to show:
 *
 *   <GenericKPIDashboard items={[
 *     { key: 'selected', label: 'Selected', value: '12/25' },
 *     { key: 'client',   label: 'Client Total', value: 12500, format: 'currency' },
 *     { key: 'builder',  label: 'Builder Total', value: summary.builder_total, format: 'currency' },
 *   ]} />
 *
 * Visibility: a KPI is rendered only when `visible !== false` AND its value
 * is present. The SERVER decides what a caller may receive (a KPI whose
 * number the caller isn't allowed to see simply arrives as `undefined`, see
 * core/field-visibility.js), so this component never shows -- or even
 * holds -- a restricted figure. `visible` exists for presentation rules
 * that are not about authorization.
 */
/** Narrowest a KPI tile gets before the bar wraps to another row. */
const KPI_TILE_MIN_WIDTH = '9rem';

export type KPIFormat = 'text' | 'number' | 'currency';

export interface KPIItem {
  key: string;
  label: string;
  value?: string | number | null;
  format?: KPIFormat;
  /** Optional small icon shown before the label. */
  icon?: React.ReactNode;
  /** Optional secondary text under the value (e.g. "of 25"). */
  hint?: string;
  visible?: boolean;
  /** The figure is not known yet: the tile keeps its place (label + a grey bar) instead of being left out, so the bar never changes its number of tiles when the data arrives. */
  loading?: boolean;
}

export function formatKPIValue(value: string | number, format: KPIFormat = 'text'): string {
  if (typeof value === 'string') return value;
  if (format === 'currency') {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  if (format === 'number') return value.toLocaleString('en-US');
  return String(value);
}

interface GenericKPIDashboardProps {
  items: KPIItem[];
  className?: string;
}

export function GenericKPIDashboard({ items, className }: GenericKPIDashboardProps) {
  const shown = items.filter((item) => item.visible !== false && (item.loading || (item.value !== undefined && item.value !== null)));
  if (shown.length === 0) return null;

  return (
    <div
      className={cn('grid gap-2', className)}
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${KPI_TILE_MIN_WIDTH}, 1fr))` }}
      data-testid="kpi-dashboard"
    >
      {shown.map((item) => (
        <div key={item.key} className="rounded-lg border border-border bg-card px-4 py-3" data-testid={`kpi-${item.key}`}>
          <div className="flex items-center gap-1.5 font-dashboard-text-muted">
            {item.icon}
            <span>{item.label}</span>
          </div>
          {item.loading ? (
            <div className="mt-2 h-5 w-16 animate-pulse rounded bg-gray-200" data-testid={`kpi-${item.key}-loading`} aria-label="Loading" />
          ) : (
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-heading-text">{formatKPIValue(item.value as string | number, item.format)}</span>
              {item.hint && <span className="font-dashboard-text-muted">{item.hint}</span>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
